import { ArrowUpRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePosts } from '../lib/api/posts';
import { fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { plainText } from '../lib/utils';

/**
 * Slim, sticky-below-Topbar ticker showing the 5 most recent posts in the
 * current locale, one at a time, with a 5 s rotating fade. Pauses on hover
 * so editors can read and click. Renders nothing when the locale has no
 * recent posts — keeps quiet locales from getting a permanent empty stripe.
 *
 * Mounted from `Topbar.tsx`, so it appears on every admin page automatically.
 */
export function NewsTicker(): JSX.Element | null {
  const locale = useLocaleStore((s) => s.current);
  const postsQ = usePosts({ locale, page: 1, pageSize: 5 });
  const items = (postsQ.data?.items ?? []).map((p) => ({
    slug: p.slug,
    title: plainText(p.title),
    publishedAt: p.publishedAt,
  }));

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Reset to first when the list shrinks beneath current index
  // (e.g. locale switch, refresh).
  useEffect(() => {
    if (index >= items.length) setIndex(0);
  }, [items.length, index]);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 5000);
    return () => clearInterval(id);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const current = items[Math.min(index, items.length - 1)];
  if (!current) return null;

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="sticky top-14 z-20 border-b border-line bg-surface/90 backdrop-blur-md supports-[backdrop-filter]:bg-surface/75"
      role="region"
      aria-label="Latest posts ticker"
      aria-live="polite"
    >
      <div className="relative flex h-11 items-center gap-3 px-4 sm:gap-4 sm:px-5">
        {/* Brand left rail — visual anchor that this is a wire feed. */}
        <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-brand" />

        <span className="shrink-0 font-mono text-[9.5px] font-semibold uppercase tracking-[0.18em] text-brand">
          ▸ Wire
        </span>

        {/* Re-keyed on slug so the fade-up keyframe replays on every change. */}
        <Link
          key={current.slug}
          to={`/posts/${current.slug}`}
          className="group flex min-w-0 flex-1 items-baseline gap-2 sm:gap-3 animate-fade-up"
        >
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium leading-tight text-ink group-hover:text-brand">
            {current.title}
          </span>
          <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-subtle sm:inline">
            {fmtRelative(current.publishedAt)}
          </span>
          <ArrowUpRight className="h-3 w-3 shrink-0 text-ink-subtle transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand" />
        </Link>

        {items.length > 1 ? (
          <div className="hidden shrink-0 items-center gap-1 sm:flex">
            {items.map((it, i) => {
              const active = i === Math.min(index, items.length - 1);
              return (
                <button
                  key={it.slug}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Show item ${i + 1} of ${items.length}`}
                  className={
                    'h-1 rounded-full transition-all ' +
                    (active ? 'w-3 bg-brand' : 'w-1 bg-line-strong hover:bg-ink-subtle')
                  }
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
